import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { Message } from 'primereact/message'
import { useId } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useDepartmentsList } from '~/hooks/departmentsApi'
import { useCreateDocument } from '~/hooks/documentsApi'
import { useUsersList } from '~/hooks/usersApi'
import { DocumentType } from '~/types/document.types'
import { DocumentTypeLabels, enumToOptions } from '~/types/feEnumLabels'

const schema = z.object({
  title: z.string().min(4, 'Title must be at least 4 characters'),
  titleId: z.string().optional(),
  docNumberTemplate: z.string().optional(),
  docType: z.nativeEnum(DocumentType),
  departmentId: z.number().min(1, 'Department is required'),
  picUserId: z.number().min(1, 'PIC is required'),
})

type FormData = z.infer<typeof schema>

export type DocumentCreateFormProps = {
  controlId?: number
  onSuccess: () => void
  onCancel: () => void
}

const DocumentCreateForm = ({ controlId, onSuccess, onCancel }: DocumentCreateFormProps) => {
  const departmentsQuery = useDepartmentsList()
  const usersQuery = useUsersList()
  const createMutation = useCreateDocument()

  const titleFieldId = useId()
  const titleIdFieldId = useId()
  const docNumberTemplateFieldId = useId()
  const docTypeFieldId = useId()
  const departmentFieldId = useId()
  const picUserIdFieldId = useId()

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      titleId: '',
      docNumberTemplate: '',
    },
  })

  const selectedDeptId = watch('departmentId')
  const filteredUsers = usersQuery.data?.filter((u) =>
    u.departments?.some((d) => d.departmentId === selectedDeptId),
  )

  const docTypeOptions = enumToOptions(DocumentTypeLabels)

  const onSubmit = (data: FormData) => {
    createMutation.mutate(
      {
        ...data,
        controlId,
      },
      {
        onSuccess,
      },
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4 mt-2'>
      <div className='flex flex-col gap-1'>
        <label htmlFor={titleFieldId} className='text-sm font-bold'>
          Title (English) *
        </label>
        <Controller
          name='title'
          control={control}
          render={({ field }) => (
            <InputText
              id={titleFieldId}
              {...field}
              className={errors.title ? 'p-invalid' : ''}
              placeholder='Enter English title'
            />
          )}
        />
        {errors.title && <Message severity='error' text={errors.title.message} />}
      </div>

      <div className='flex flex-col gap-1'>
        <label htmlFor={titleIdFieldId} className='text-sm font-bold'>
          Title (Indonesian)
        </label>
        <Controller
          name='titleId'
          control={control}
          render={({ field }) => (
            <InputText id={titleIdFieldId} {...field} placeholder='Enter Indonesian title' />
          )}
        />
      </div>

      <div className='flex flex-col gap-1'>
        <label htmlFor={docNumberTemplateFieldId} className='text-sm font-bold'>
          Document Number Template
        </label>
        <Controller
          name='docNumberTemplate'
          control={control}
          render={({ field }) => (
            <InputText id={docNumberTemplateFieldId} {...field} placeholder='e.g. DOC-{REV}' />
          )}
        />
        <small className='text-gray-500'>
          Use {'{REV}'} as a placeholder for the revision number.
        </small>
      </div>

      <div className='grid grid-cols-2 gap-4'>
        <div className='flex flex-col gap-1'>
          <label htmlFor={docTypeFieldId} className='text-sm font-bold'>
            Document Type *
          </label>
          <Controller
            name='docType'
            control={control}
            render={({ field }) => (
              <Dropdown
                id={docTypeFieldId}
                {...field}
                options={docTypeOptions}
                placeholder='Select Type'
                className={errors.docType ? 'p-invalid' : ''}
              />
            )}
          />
          {errors.docType && <Message severity='error' text={errors.docType.message} />}
        </div>

        <div className='flex flex-col gap-1'>
          <label htmlFor={departmentFieldId} className='text-sm font-bold'>
            Department *
          </label>
          <Controller
            name='departmentId'
            control={control}
            render={({ field }) => (
              <Dropdown
                id={departmentFieldId}
                {...field}
                options={departmentsQuery.data}
                optionLabel='title'
                optionValue='id'
                placeholder='Select Department'
                className={errors.departmentId ? 'p-invalid' : ''}
                loading={departmentsQuery.isLoading}
              />
            )}
          />
          {errors.departmentId && <Message severity='error' text={errors.departmentId.message} />}
        </div>
      </div>

      <div className='flex flex-col gap-1'>
        <label htmlFor={picUserIdFieldId} className='text-sm font-bold'>
          Person In Charge (PIC) *
        </label>
        <Controller
          name='picUserId'
          control={control}
          render={({ field }) => (
            <Dropdown
              id={picUserIdFieldId}
              {...field}
              options={filteredUsers}
              optionLabel='name'
              optionValue='id'
              placeholder={selectedDeptId ? 'Select PIC' : 'Select Department first'}
              disabled={!selectedDeptId}
              className={errors.picUserId ? 'p-invalid' : ''}
              loading={usersQuery.isLoading}
            />
          )}
        />
        {errors.picUserId && <Message severity='error' text={errors.picUserId.message} />}
      </div>

      <div className='flex justify-end gap-2 mt-4'>
        <Button label='Cancel' type='button' severity='secondary' onClick={onCancel} />
        <Button label='Create Document' type='submit' loading={createMutation.isLoading} />
      </div>
    </form>
  )
}

export default DocumentCreateForm
